import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { ToastContainer, ToastType } from '../components/Toast';
import AttributeLabel from '../components/AttributeLabel';
import { ModalShell, ModalActions } from '../components/modals/core';
import {
  FolderIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  LinkIcon,
  PhotoIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ClockIcon,
  PlusIcon,
  XCircleIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';

// Interfaces
interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  created_at: string;
  creator_name: string;
  image_path?: string;
  voting_deadline?: string;
  min_price?: number;
  max_price?: number;
  variant_count?: number;
  helper_count?: number;
  items?: ProjectItem[];
}

interface ProjectItem {
  id: number;
  project_id: number;
  name: string;
  description: string;
  quantity: number;
  position_order: number;
  variants?: ItemVariant[];
}

interface ItemVariant {
  id: number;
  project_item_id: number;
  name: string;
  description: string;
  price: number | string;
  image_path?: string;
  link?: string;
  vote_count: number;
  total_votes: number;
  my_vote?: number;
  creator_name?: string;
}

const MyProjects: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [helperStatusMap, setHelperStatusMap] = useState<Record<number, boolean>>({});
  const [helperLoadingMap, setHelperLoadingMap] = useState<Record<number, boolean>>({});

  // Toast-System
  const [toasts, setToasts] = useState<Array<{ id: string; type: ToastType; message: string }>>([]);

  const addToast = (type: ToastType, message: string) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, type, message }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Grid Layout State - Anzahl der Spalten für Varianten-Ansicht (2-6)
  const [gridColumns, setGridColumns] = useState<number>(() => {
    const saved = localStorage.getItem('myProjectsGridColumns');
    return saved ? parseInt(saved, 10) : 3;
  });

  const handleGridColumnsChange = (columns: number) => {
    setGridColumns(columns);
    localStorage.setItem('myProjectsGridColumns', columns.toString());
  };

  // Grid class helper
  const getGridClass = (): string => {
    const baseClass = 'grid grid-cols-1';

    // Tablet: max 3 Spalten
    let mdClass = 'md:grid-cols-2';
    if (gridColumns === 3) mdClass = 'md:grid-cols-3';

    // Desktop: volle Kontrolle, aber 5-6 Spalten erst ab xl
    let lgClass = '';
    switch(gridColumns) {
      case 2: lgClass = 'lg:grid-cols-2'; break;
      case 3: lgClass = 'lg:grid-cols-3'; break;
      case 4: lgClass = 'lg:grid-cols-4'; break;
      case 5: lgClass = 'lg:grid-cols-4 xl:grid-cols-5'; break;
      case 6: lgClass = 'lg:grid-cols-4 xl:grid-cols-6'; break;
      default: lgClass = 'lg:grid-cols-3';
    }

    return `${baseClass} ${mdClass} ${lgClass}`;
  };

  // Dynamisches Card-Spacing basierend auf Spaltenanzahl
  const getCardSpacing = (): string => {
    if (gridColumns <= 3) return 'p-5';
    if (gridColumns === 4) return 'p-4';
    return 'p-3'; // 5-6 Spalten
  };

  // Dynamischer Grid-Gap
  const getGridGap = (): string => {
    if (gridColumns <= 3) return 'gap-6';
    if (gridColumns === 4) return 'gap-4';
    return 'gap-3'; // 5-6 Spalten
  };

  // Dynamische Bild-Höhe
  const getImageHeight = (): string => {
    if (gridColumns <= 3) return 'h-56';
    if (gridColumns === 4) return 'h-48';
    return 'h-40'; // 5-6 Spalten
  };

  // Dynamische Text-Größen
  const getTextSizes = () => ({
    name: gridColumns <= 4 ? 'text-lg' : 'text-base',
    price: gridColumns <= 4 ? 'text-lg' : 'text-base',
    description: gridColumns <= 4 ? 'text-sm' : 'text-xs',
  });

  // States für Varianten-Modal
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ProjectItem | null>(null);
  const [variantForm, setVariantForm] = useState({
    name: '',
    description: '',
    price: '',
    link: ''
  });
  const [selectedVariantImage, setSelectedVariantImage] = useState<File | null>(null);
  const [variantImagePreview, setVariantImagePreview] = useState<string | null>(null);

  // Helper function to check if voting deadline has passed
  const isVotingClosed = (deadline?: string): boolean => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  // Helper function to format remaining time
  const getRemainingTime = (deadline?: string): string => {
    if (!deadline) return '';

    const now = new Date();
    const end = new Date(deadline);
    const diff = end.getTime() - now.getTime();

    if (diff <= 0) return 'Abstimmung beendet';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Noch ${days} Tag${days > 1 ? 'e' : ''} und ${hours} Stunden`;
    if (hours > 0) return `Noch ${hours} Stunde${hours > 1 ? 'n' : ''} und ${minutes} Minuten`;
    return `Noch ${minutes} Minute${minutes > 1 ? 'n' : ''}`;
  };

  // Load projects
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/projects');
      if (response.data.success) {
        // Nur aktive Projekte anzeigen
        const activeProjects = response.data.projects.filter((p: Project) => p.status === 'active');
        setProjects(activeProjects);

        // Helfer-Status für alle Projekte laden
        loadAllHelperStatuses(activeProjects);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Projekte:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectDetails = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}`);
      if (response.data.success) {
        setSelectedProject(response.data.project);
        // Helfer-Status laden
        loadHelperStatus(projectId);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Projekts:', error);
    }
  };

  // Helfer-Status für alle Projekte laden
  const loadAllHelperStatuses = async (projects: Project[]) => {
    const statusMap: Record<number, boolean> = {};

    for (const project of projects) {
      try {
        const response = await apiClient.get(`/api/projects/${project.id}/helpers/me`);
        if (response.data.success) {
          statusMap[project.id] = response.data.isHelper;
        }
      } catch (error) {
        console.error(`Fehler beim Laden des Helfer-Status für Projekt ${project.id}:`, error);
      }
    }

    setHelperStatusMap(statusMap);
  };

  // Helfer-Status für ein einzelnes Projekt laden
  const loadHelperStatus = async (projectId: number) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/helpers/me`);
      if (response.data.success) {
        setHelperStatusMap(prev => ({ ...prev, [projectId]: response.data.isHelper }));
      }
    } catch (error) {
      console.error(`Fehler beim Laden des Helfer-Status für Projekt ${projectId}:`, error);
    }
  };

  // Als Helfer registrieren/deregistrieren
  const toggleHelperStatus = async (projectId: number, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Verhindert, dass das Projekt geöffnet wird
    }

    setHelperLoadingMap(prev => ({ ...prev, [projectId]: true }));
    const currentStatus = helperStatusMap[projectId] || false;

    try {
      if (currentStatus) {
        // Helfer-Registrierung entfernen
        const response = await apiClient.delete(`/api/projects/${projectId}/helpers`);
        if (response.data.success) {
          setHelperStatusMap(prev => ({ ...prev, [projectId]: false }));
          addToast('success', 'Sie haben sich erfolgreich abgemeldet');
          // Projekte neu laden um die Helfer-Anzahl zu aktualisieren
          loadProjects();
        }
      } else {
        // Als Helfer registrieren
        const response = await apiClient.post(`/api/projects/${projectId}/helpers`, {});
        if (response.data.success) {
          setHelperStatusMap(prev => ({ ...prev, [projectId]: true }));
          addToast('success', 'Sie haben sich erfolgreich als Helfer registriert');
          // Projekte neu laden um die Helfer-Anzahl zu aktualisieren
          loadProjects();
        }
      }
    } catch (error) {
      console.error('Fehler beim Ändern des Helfer-Status:', error);
      addToast('error', 'Es ist ein Fehler aufgetreten');
    } finally {
      setHelperLoadingMap(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Voting
  const handleVote = async (variantId: number, voteValue: number) => {
    try {
      const response = await apiClient.post(`/api/projects/variants/${variantId}/vote`, { vote_value: voteValue });
      if (response.data.success && selectedProject) {
        loadProjectDetails(selectedProject.id);
      }
    } catch (error) {
      console.error('Fehler beim Abstimmen:', error);
    }
  };

  // Varianten-Modal öffnen
  const openAddVariant = (item: ProjectItem) => {
    setSelectedItem(item);
    setVariantForm({ name: '', description: '', price: '', link: '' });
    setSelectedVariantImage(null);
    setVariantImagePreview(null);
    setShowVariantModal(true);
  };

  // Bild-Handling für Variante
  const handleVariantImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedVariantImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVariantImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVariantPaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            setSelectedVariantImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
              setVariantImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    }
  };

  // Variante speichern
  const handleSaveVariant = async () => {
    if (!selectedItem) return;

    try {
      const formData = new FormData();
      formData.append('name', variantForm.name);
      formData.append('description', variantForm.description);
      formData.append('price', variantForm.price);
      formData.append('link', variantForm.link);

      if (selectedVariantImage) {
        formData.append('image', selectedVariantImage);
      }

      const response = await apiClient.post(`/api/projects/items/${selectedItem.id}/variants`, formData);

      if (response.data.success) {
        if (selectedProject) {
          loadProjectDetails(selectedProject.id);
        }
        setShowVariantModal(false);
        setVariantForm({ name: '', description: '', price: '', link: '' });
        setSelectedVariantImage(null);
        setVariantImagePreview(null);
        addToast('success', 'Variante erfolgreich hinzugefügt!');
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern der Variante:', error);
      const errorMessage = error.response?.data?.message || 'Ein unbekannter Fehler ist aufgetreten';
      addToast('error', errorMessage);
    }
  };

  // Hilfsfunktion um String oder Number zu Number zu konvertieren
  const toNumber = (value: string | number | undefined | null): number => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getImageUrl = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}/api/projects/image/${encodeURIComponent(imagePath)}`;
  };

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header - Variante 1: Einfacher Header */}
        <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
          <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <FolderIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
              <div>
                <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Vereinsprojekte</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Vereinsprojekte ansehen und abstimmen</p>
              </div>
            </div>
          </div>
        </header>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Projects List */}
        {!selectedProject ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                className="card hover:shadow-lg transition-shadow cursor-pointer overflow-hidden p-0"
                onClick={() => loadProjectDetails(project.id)}
              >
                {/* Project Image */}
                <div className="relative h-56 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  {project.image_path ? (
                    <>
                      <img
                        src={getImageUrl(project.image_path)}
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.style.display = 'none';
                          const fallback = img.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                        <FolderIcon className="h-24 w-24 text-gray-400 dark:text-gray-500" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderIcon className="h-24 w-24 text-gray-400 dark:text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {project.name}
                      </h3>
                      <p className="mt-2 text-gray-600 dark:text-gray-400">
                        {project.description}
                      </p>
                    </div>
                  </div>

                  {/* Price Range */}
                  {(project.variant_count ?? 0) > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4 bg-gray-50/50 dark:bg-gray-800/50">
                      <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <AttributeLabel iconKey="amount" size="sm">Preisspanne</AttributeLabel>
                        <span className="text-xs">
                          {project.variant_count} {project.variant_count === 1 ? 'Variante' : 'Varianten'}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
                        {project.min_price === project.max_price ? (
                          <span className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            {toNumber(project.min_price).toFixed(2)} €
                          </span>
                        ) : (
                          <>
                            <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                              {toNumber(project.min_price).toFixed(2)} €
                            </span>
                            <span className="text-gray-400">—</span>
                            <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                              {toNumber(project.max_price).toFixed(2)} €
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300">
                      Aktiv
                    </span>
                    {project.voting_deadline && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isVotingClosed(project.voting_deadline)
                          ? 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300'
                          : 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300'
                      }`}>
                        {getRemainingTime(project.voting_deadline)}
                      </span>
                    )}
                    {(project.helper_count ?? 0) > 0 && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 flex items-center gap-1">
                        <HandRaisedIcon className="h-3 w-3" />
                        {project.helper_count} {project.helper_count === 1 ? 'Helfer' : 'Helfer'}
                      </span>
                    )}
                  </div>

                  {/* Helper Button */}
                  <div className="mt-4">
                    <button
                      onClick={(e) => toggleHelperStatus(project.id, e)}
                      disabled={helperLoadingMap[project.id]}
                      className={`w-full btn text-sm sm:text-base font-medium flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 min-h-[44px] ${
                        helperStatusMap[project.id]
                          ? 'bg-success-600 hover:bg-success-700 text-white'
                          : 'bg-primary-600 hover:bg-primary-700 text-white'
                      } ${helperLoadingMap[project.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {helperLoadingMap[project.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white flex-shrink-0"></div>
                          <span className="whitespace-nowrap">Lädt...</span>
                        </>
                      ) : (
                        <>
                          <HandRaisedIcon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                          <span className="whitespace-nowrap">
                            {helperStatusMap[project.id] ? 'Sie helfen mit' : 'Ich möchte mithelfen'}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {projects.length === 0 && !loading && (
              <div className="col-span-2 text-center py-12">
                <FolderIcon className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Keine aktiven Projekte vorhanden</p>
              </div>
            )}
          </div>
        ) : (
          /* Project Details View */
          <div className="space-y-6">
            {/* Back Button */}
            <button
              onClick={() => setSelectedProject(null)}
              className="text-primary-600 hover:text-primary-700 flex items-center gap-2"
            >
              ← Zurück zur Übersicht
            </button>

            {/* Project Header */}
            <div className="card overflow-hidden p-0">
              {/* Project Image */}
              <div className="relative h-64 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                {selectedProject.image_path ? (
                  <>
                    <img
                      src={getImageUrl(selectedProject.image_path)}
                      alt={selectedProject.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full flex items-center justify-center" style={{ display: 'none' }}>
                      <FolderIcon className="h-32 w-32 text-gray-400 dark:text-gray-500" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FolderIcon className="h-32 w-32 text-gray-400 dark:text-gray-500" />
                  </div>
                )}
              </div>
              <div className="p-6">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedProject.name}
                  </h2>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                    {selectedProject.description}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Erstellt von: {selectedProject.creator_name}
                  </p>

                  {/* Info-Bereich mit Countdown und Helfer-Button */}
                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    {selectedProject.voting_deadline && (
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
                        isVotingClosed(selectedProject.voting_deadline)
                          ? 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300'
                          : 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300'
                      }`}>
                        <ClockIcon className="h-5 w-5" />
                        <span className="font-semibold">{getRemainingTime(selectedProject.voting_deadline)}</span>
                      </div>
                    )}

                    <button
                      onClick={() => toggleHelperStatus(selectedProject.id)}
                      disabled={helperLoadingMap[selectedProject.id]}
                      className={`btn flex-shrink-0 text-sm sm:text-base font-medium flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 min-h-[44px] sm:ml-auto ${
                        helperStatusMap[selectedProject.id]
                          ? 'bg-success-600 hover:bg-success-700 text-white'
                          : 'bg-primary-600 hover:bg-primary-700 text-white'
                      } ${helperLoadingMap[selectedProject.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {helperLoadingMap[selectedProject.id] ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white flex-shrink-0"></div>
                          <span className="whitespace-nowrap">Lädt...</span>
                        </>
                      ) : (
                        <>
                          <HandRaisedIcon className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                          <span className="whitespace-nowrap">
                            {helperStatusMap[selectedProject.id] ? 'Sie helfen bereits mit' : 'Ich möchte mithelfen'}
                          </span>
                        </>
                      )}
                    </button>
                  </div>

                  {helperStatusMap[selectedProject.id] && (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                      Der Vorstand wurde über Ihre Bereitschaft informiert.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Items List */}
            {selectedProject.items && selectedProject.items.map((item) => (
              <div key={item.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <ClipboardDocumentListIcon className="h-6 w-6" />
                      {item.name}
                      <span className="text-sm font-normal text-gray-500">
                        ({item.quantity}x)
                      </span>
                    </h3>
                    {item.description && (
                      <p className="mt-1 text-gray-600 dark:text-gray-400">{item.description}</p>
                    )}
                  </div>
                  {!isVotingClosed(selectedProject.voting_deadline) && (
                    <button
                      onClick={() => openAddVariant(item)}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <PlusIcon className="h-5 w-5" />
                      <span className="hidden sm:inline">Variante hinzufügen</span>
                    </button>
                  )}
                </div>

                {/* Variants */}
                {item.variants && item.variants.length > 0 && (
                  <>
                    {/* Grid Layout Controls */}
                    <div className="mb-6 hidden md:flex items-center gap-3 justify-end">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:inline">Ansicht:</span>
                      <div className="inline-flex gap-1.5 bg-white dark:bg-gray-800 rounded-lg p-1.5 shadow-sm border border-gray-200 dark:border-gray-700">
                        {[2, 3, 4, 5, 6].map((cols) => (
                          <button
                            key={cols}
                            onClick={() => handleGridColumnsChange(cols)}
                            className={`min-w-[2.5rem] px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                              gridColumns === cols
                                ? 'bg-primary-600 text-white shadow-md scale-105'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                            }`}
                            title={`${cols} Spalten anzeigen`}
                            aria-label={`${cols} Spalten anzeigen`}
                            aria-pressed={gridColumns === cols}
                          >
                            {cols}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className={`${getGridClass()} ${getGridGap()}`}>
                    {item.variants.map((variant) => {
                      const textSizes = getTextSizes();
                      return (
                      <div
                        key={variant.id}
                        className="relative bg-white dark:bg-surface-dark rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 group"
                      >
                        {/* Image or Placeholder */}
                        <div className={`relative ${getImageHeight()} bg-gray-200 dark:bg-gray-800 overflow-hidden`}>
                          {variant.image_path ? (
                            <img
                              src={getImageUrl(variant.image_path)}
                              alt={variant.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <PhotoIcon className="h-20 w-20 text-gray-400 dark:text-gray-500" />
                            </div>
                          )}

                          {/* Vote Badge */}
                          <div className="absolute top-3 right-3 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg">
                            <div className="flex items-center gap-1.5">
                              <ChartBarIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {variant.total_votes}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className={getCardSpacing()}>
                          <h4 className={`${textSizes.name} font-bold text-gray-900 dark:text-white mb-2 line-clamp-1`}>
                            {variant.name}
                          </h4>

                          {variant.creator_name && (
                            <div className="mb-2">
                              <span className={`inline-flex items-center rounded-full font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 ${
                                gridColumns <= 4 ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.5 text-[10px]'
                              }`}>
                                {gridColumns <= 4 ? `Von ${variant.creator_name}` : variant.creator_name}
                              </span>
                            </div>
                          )}

                          {gridColumns <= 4 && variant.description && (
                            <p className={`${textSizes.description} text-gray-600 dark:text-gray-400 mb-3 line-clamp-2`}>
                              {variant.description}
                            </p>
                          )}

                          {/* Price Section */}
                          <div className={`bg-primary-50 dark:bg-primary-900/20 rounded-lg mb-3 ${gridColumns <= 4 ? 'p-3' : 'p-2'}`}>
                            <div className="flex items-baseline gap-1.5">
                              <span className={`${textSizes.price} font-bold text-primary-600 dark:text-primary-400`}>
                                {toNumber(variant.price).toFixed(2)} €
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                pro Stück
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              Gesamt: <span className="font-semibold">{(toNumber(variant.price) * item.quantity).toFixed(2)} €</span>
                            </div>
                          </div>

                          {/* Link */}
                          {variant.link && (
                            <a
                              href={variant.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium mb-4 group/link"
                            >
                              <LinkIcon className="h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
                              Link öffnen
                            </a>
                          )}

                          {/* Voting Buttons */}
                          <div className="flex gap-2 mt-4">
                            {isVotingClosed(selectedProject.voting_deadline) ? (
                              <div className={`w-full text-center rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium ${
                                gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3 text-sm'
                              }`}>
                                Abstimmung beendet
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleVote(variant.id, 1)}
                                  className={`flex-1 flex items-center justify-center rounded-lg transition-all duration-200 ${
                                    gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                                  } ${
                                    variant.my_vote === 1
                                      ? 'bg-success-500 text-white shadow-lg shadow-success-500/30 scale-[1.02]'
                                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-success-50 dark:hover:bg-success-900/20 hover:text-success-600 dark:hover:text-success-400 hover:scale-[1.02]'
                                  }`}
                                  title="Dafür stimmen"
                                  aria-label="Für diese Variante stimmen"
                                  aria-pressed={variant.my_vote === 1}
                                >
                                  <HandThumbUpIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                                </button>
                                <button
                                  onClick={() => handleVote(variant.id, -1)}
                                  className={`flex-1 flex items-center justify-center rounded-lg transition-all duration-200 ${
                                    gridColumns <= 4 ? 'py-3 px-4' : 'py-2 px-3'
                                  } ${
                                    variant.my_vote === -1
                                      ? 'bg-error-500 text-white shadow-lg shadow-error-500/30 scale-[1.02]'
                                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-error-50 dark:hover:bg-error-900/20 hover:text-error-600 dark:hover:text-error-400 hover:scale-[1.02]'
                                  }`}
                                  title="Dagegen stimmen"
                                  aria-label="Gegen diese Variante stimmen"
                                  aria-pressed={variant.my_vote === -1}
                                >
                                  <HandThumbDownIcon className={gridColumns <= 4 ? 'h-6 w-6' : 'h-5 w-5'} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Varianten-Modal */}
      {showVariantModal && selectedItem && (
        <ModalShell
          open
          onClose={() => {
            setShowVariantModal(false);
            setVariantForm({ name: '', description: '', price: '', link: '' });
            setSelectedVariantImage(null);
            setVariantImagePreview(null);
          }}
          variant="form"
          size="md"
          title={`Variante hinzufügen zu "${selectedItem.name}"`}
          icon={PlusIcon}
          closeOnBackdrop={false}
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setShowVariantModal(false);
                  setVariantForm({ name: '', description: '', price: '', link: '' });
                  setSelectedVariantImage(null);
                  setVariantImagePreview(null);
                },
              }}
              primary={{
                label: 'Variante hinzufügen',
                onClick: handleSaveVariant,
                disabled: !variantForm.name || !variantForm.price,
              }}
            />
          }
        >
          <div className="space-y-4">
            <div>
              <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Varianten-Name *
              </AttributeLabel>
              <input
                type="text"
                value={variantForm.name}
                onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                className="input w-full"
                placeholder="z.B. Variante A"
              />
            </div>
            <div>
              <AttributeLabel iconKey="description" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Beschreibung
              </AttributeLabel>
              <textarea
                value={variantForm.description}
                onChange={(e) => setVariantForm({ ...variantForm, description: e.target.value })}
                className="input w-full h-24"
                placeholder="Beschreibung der Variante..."
              />
            </div>
            <div>
              <AttributeLabel iconKey="amount" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Preis pro Stück * (€)
              </AttributeLabel>
              <input
                type="number"
                step="0.01"
                value={variantForm.price}
                onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                className="input w-full"
                placeholder="0.00"
              />
            </div>
            <div>
              <AttributeLabel iconKey="link" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Link (optional)
              </AttributeLabel>
              <input
                type="url"
                value={variantForm.link}
                onChange={(e) => setVariantForm({ ...variantForm, link: e.target.value })}
                className="input w-full"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bild (optional)
              </label>

              {/* Paste Area */}
              <div
                onPaste={handleVariantPaste}
                tabIndex={0}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 mb-3 text-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span className="font-semibold text-primary-600 dark:text-primary-400">
                    Klicken Sie hier und drücken Sie Strg+V
                  </span>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  um ein Bild aus der Zwischenablage einzufügen
                </p>
              </div>

              {/* File Input */}
              <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                oder
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleVariantImageChange}
                className="input w-full"
              />

              {/* Preview */}
              {variantImagePreview && (
                <div className="mt-4 relative">
                  <img
                    src={variantImagePreview}
                    alt="Vorschau"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => {
                      setSelectedVariantImage(null);
                      setVariantImagePreview(null);
                    }}
                    className="absolute top-2 right-2 p-2 bg-error-500 hover:bg-error-600 text-white rounded-full transition-colors"
                    title="Bild entfernen"
                    type="button"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </ModalShell>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default MyProjects;
